import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2 } from 'lucide-react';
import type { CalendarEvent, CreateCalendarEventInput } from '@frappi/shared';
import { createCalendarEventSchema } from '@frappi/shared';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useStudents } from '@/features/students/hooks/use-students';

const COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B',
  '#EF4444', '#EC4899', '#06B6D4', '#84CC16',
  '#93C5FD', '#6EE7B7', '#C4B5FD', '#FDE68A',
  '#FCA5A5', '#F9A8D4', '#67E8F9', '#BEF264',
];

interface EventFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: CalendarEvent | null;
  defaultDate?: string;
  onSubmit: (data: CreateCalendarEventInput) => void;
  onDelete?: () => void;
  isLoading?: boolean;
  isDeleting?: boolean;
}

export function EventForm({
  open,
  onOpenChange,
  event,
  defaultDate,
  onSubmit,
  onDelete,
  isLoading,
  isDeleting,
}: EventFormProps) {
  const { t } = useTranslation('calendar');
  const { data: students } = useStudents();
  const isEditing = !!event;

  const form = useForm<CreateCalendarEventInput>({
    resolver: zodResolver(createCalendarEventSchema),
    defaultValues: {
      title: '',
      description: '',
      studentId: 'none',
      eventType: 'other',
      startDate: defaultDate || new Date().toISOString().split('T')[0],
      endDate: undefined,
      startTime: undefined,
      endTime: undefined,
      allDay: true,
      color: undefined,
    },
  });

  useEffect(() => {
    if (event) {
      form.reset({
        title: event.title,
        description: event.description || '',
        studentId: event.studentId || 'none',
        eventType: event.eventType,
        startDate: event.startDate,
        endDate: event.endDate || undefined,
        startTime: event.startTime || undefined,
        endTime: event.endTime || undefined,
        allDay: event.allDay,
        color: event.color || undefined,
      });
    } else {
      form.reset({
        title: '',
        description: '',
        studentId: 'none',
        eventType: 'other',
        startDate: defaultDate || new Date().toISOString().split('T')[0],
        endDate: undefined,
        startTime: undefined,
        endTime: undefined,
        allDay: true,
        color: undefined,
      });
    }
  }, [event, defaultDate, form]);

  const handleSubmit = (data: CreateCalendarEventInput) => {
    // Prevent double submission
    if (isLoading) return;
    const cleanedData: CreateCalendarEventInput = {
      ...data,
      description: data.description || undefined,
      studentId: data.studentId === 'none' ? undefined : data.studentId || undefined,
      endDate: data.endDate || undefined,
      startTime: data.allDay ? undefined : data.startTime || undefined,
      endTime: data.allDay ? undefined : data.endTime || undefined,
      color: data.color || undefined,
    };
    onSubmit(cleanedData);
  };

  const allDay = form.watch('allDay');
  const selectedColor = form.watch('color');

  const eventTypes = [
    'school_day',
    'vacation',
    'holiday',
    'exam',
    'meeting',
    'activity',
    'other',
  ] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editEvent') : t('addEvent')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('form.editDescription')
              : t('form.addDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.title')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('form.titlePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('form.descriptionPlaceholder')}
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField
                control={form.control}
                name="eventType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.eventType')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {eventTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(`eventType.${type}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="studentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.student')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('form.optional')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">{t('form.none')}</SelectItem>
                        {students?.map((student) => (
                          <SelectItem key={student.id} value={student.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: student.identifyingColor }}
                              />
                              {student.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="allDay"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{t('form.allDay')}</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.startDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.endDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!allDay && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.startTime')}</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.endTime')}</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.color')}</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`h-7 w-7 sm:h-8 sm:w-8 rounded-lg transition-all ${
                            selectedColor === color
                              ? 'ring-2 ring-offset-2 ring-primary scale-110'
                              : 'hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                          aria-label={`Seleccionar color ${color}`}
                          aria-pressed={selectedColor === color}
                        />
                      ))}
                      {selectedColor && (
                        <button
                          type="button"
                          className="h-8 px-3 text-xs rounded-lg border hover:bg-accent"
                          onClick={() => field.onChange(undefined)}
                          aria-label={t('form.removeColor')}
                        >
                          {t('form.remove')}
                        </button>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4 flex-col-reverse sm:flex-row sm:justify-between gap-2">
              {isEditing && onDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onDelete}
                  disabled={isLoading || isDeleting}
                  className="w-full sm:w-auto"
                >
                  {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('form.delete')}
                </Button>
              )}
              <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto sm:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading || isDeleting}
                  className="w-full sm:w-auto"
                >
                  {t('form.cancel')}
                </Button>
                <Button type="submit" disabled={isLoading || isDeleting} className="w-full sm:w-auto">
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEditing ? t('form.save') : t('form.create')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
